import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Input, Card, Progress, Alert, Spin, Tooltip } from 'antd';
import { 
  SearchOutlined, 
  SecurityScanOutlined, 
  DollarOutlined,
  WarningOutlined,
  LockOutlined,
  CheckCircleOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthProvider';
import ClumseAIChatWidget from '../components/clumse-ai/ClumseAIChatWidget';

const ForensicShadowAudit = () => {
  const [address, setAddress] = useState('');
  const [auditPhase, setAuditPhase] = useState('claim'); // 'claim', 'verification', 'generating', 'preview', 'locked'
  const [propertyData, setPropertyData] = useState(null);
  const [auditReport, setAuditReport] = useState(null);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [chatVisible, setChatVisible] = useState(false);
  const [diagnosticAccuracy, setDiagnosticAccuracy] = useState(45); // TCS score
  const [showPaymentTrigger, setShowPaymentTrigger] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleClaimAudit = async () => {
    if (!address.trim()) return;
    
    setAuditPhase('verification');
    // Simulate property data fetching
    setTimeout(() => {
      setPropertyData({
        address: address,
        year_built: 1925,
        estimated_units: 4,
        property_type: 'fourplex',
        zip_code: '63108'
      });
      setChatVisible(true);
    }, 1500);
  };

  const handleVerificationComplete = () => {
    setAuditPhase('generating');
    setVerificationProgress(0);
    
    // Simulate audit generation with progress
    const progressInterval = setInterval(() => {
      setVerificationProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          generateMockAuditReport();
          setAuditPhase('preview');
          return 100;
        }
        return prev + 12;
      });
    }, 300);
  };

  const generateMockAuditReport = () => {
    setAuditReport({
      risk_score: 78,
      critical_failures: [
        {
          system: 'Plumbing',
          issue: 'Cast Iron Stack Fatigue',
          cost_range: '$8,000 - $25,000',
          urgency: 'Urgent'
        },
        {
          system: 'Electrical', 
          issue: 'Knob & Tube Hazard',
          cost_range: '$12,000 - $30,000',
          urgency: 'Critical'
        }
      ],
      five_year_forecast: '$89,500',
      vendor_estimates: {
        emergency_rate: '$225/hour',
        major_systems_total: '$67,000'
      }
    });
  };

  const handleUnlockFullReport = () => {
    if (user) {
      setAuditPhase('unlocked');
    } else {
      navigate('/signup?source=forensic_audit&address=' + encodeURIComponent(address));
    }
  };

  return (
    <PageContainer>
      <AuditHeader>
        <HeaderIcon>
          <SecurityScanOutlined />
        </HeaderIcon>
        <HeaderContent>
          <HeaderTitle>Forensic Shadow Audit</HeaderTitle>
          <HeaderSubtitle>
            Uncover hidden maintenance risks before they become costly emergencies
          </HeaderSubtitle>
        </HeaderContent>
      </AuditHeader>

      {auditPhase === 'claim' && (
        <ClaimSection>
          <ClaimCard>
            <ClaimTitle>Claim Your Free Property Risk Analysis</ClaimTitle>
            <ClaimDescription>
              Get a comprehensive forensic audit revealing potential maintenance failures, 
              5-year CapEx forecasts, and localized St. Louis repair cost estimates.
            </ClaimDescription>
            
            <AddressInputGroup>
              <AddressInput
                size="large"
                placeholder="Enter your St. Louis property address..."
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onPressEnter={handleClaimAudit}
                prefix={<SearchOutlined />}
              />
              <ClaimButton 
                type="primary" 
                size="large"
                onClick={handleClaimAudit}
                disabled={!address.trim()}
              >
                Start Forensic Analysis
              </ClaimButton>
            </AddressInputGroup>

            <ValuePropsGrid>
              <ValueProp>
                <PropIcon><SecurityScanOutlined /></PropIcon>
                <PropTitle>Mechanical Entropy Analysis</PropTitle>
                <PropDesc>AI-powered assessment of system degradation</PropDesc>
              </ValueProp>
              <ValueProp>
                <PropIcon><DollarOutlined /></PropIcon>
                <PropTitle>St. Louis Cost Database</PropTitle>
                <PropDesc>Localized repair estimates and vendor rates</PropDesc>
              </ValueProp>
              <ValueProp>
                <PropIcon><WarningOutlined /></PropIcon>
                <PropTitle>Critical Failure Detection</PropTitle>
                <PropDesc>Identify issues before they become emergencies</PropDesc>
              </ValueProp>
            </ValuePropsGrid>
          </ClaimCard>
        </ClaimSection>
      )}

      {auditPhase === 'verification' && (
        <VerificationSection>
          <VerificationCard>
            <VerificationTitle>Calibrating Your Forensic Analysis</VerificationTitle>
            <VerificationDesc>
              Our AI needs to ask a few targeted questions about your property 
              to ensure maximum accuracy in the risk assessment.
            </VerificationDesc>
            
            {propertyData && (
              <PropertySummary>
                <SummaryTitle>Property Detected:</SummaryTitle>
                <SummaryDetails>
                  <DetailItem><strong>Address:</strong> {propertyData.address}</DetailItem>
                  <DetailItem><strong>Built:</strong> {propertyData.year_built}</DetailItem>
                  <DetailItem><strong>Type:</strong> {propertyData.property_type}</DetailItem>
                  <DetailItem><strong>Units:</strong> {propertyData.estimated_units}</DetailItem>
                </SummaryDetails>
              </PropertySummary>
            )}

            <ChatInstructions>
              💡 <strong>Forensic Questions Coming:</strong> Answer 3-4 quick questions below 
              to calibrate the mechanical entropy analysis for your specific property.
            </ChatInstructions>

            {chatVisible && (
              <DiagnosticAccuracyCard>
                <DiagnosticHeader>
                  <ExperimentOutlined style={{ color: '#ff5722', fontSize: '18px' }} />
                  <DiagnosticTitle>Diagnostic Accuracy</DiagnosticTitle>
                </DiagnosticHeader>
                <DiagnosticProgress>
                  <Progress 
                    type="circle"
                    percent={diagnosticAccuracy}
                    size={80}
                    strokeColor={
                      diagnosticAccuracy >= 90 ? '#52c41a' :
                      diagnosticAccuracy >= 75 ? '#faad14' : 
                      '#ff4d4f'
                    }
                    format={percent => (
                      <DiagnosticScore accurate={diagnosticAccuracy >= 90}>
                        {percent}%
                      </DiagnosticScore>
                    )}
                  />
                  <DiagnosticLabel accurate={diagnosticAccuracy >= 90}>
                    {diagnosticAccuracy >= 90 ? 'High Certainty' :
                     diagnosticAccuracy >= 75 ? 'Good Confidence' :
                     diagnosticAccuracy >= 50 ? 'Building Profile' : 'Initial Assessment'}
                  </DiagnosticLabel>
                </DiagnosticProgress>
                
                {showPaymentTrigger && (
                  <PaymentTriggerAlert>
                    <Alert
                      message="Diagnostic certainty reached. 50 vendors identified for this specific profile."
                      type="success"
                      showIcon
                      action={
                        <Button 
                          size="small" 
                          type="primary" 
                          onClick={() => navigate('/subscription?source=tcs_trigger')}
                        >
                          Use search
                        </Button>
                      }
                    />
                  </PaymentTriggerAlert>
                )}
              </DiagnosticAccuracyCard>
            )}
          </VerificationCard>

          {chatVisible && (
            <ChatContainer>
              <ClumseAIChatWidget
                isVisible={true}
                title="Forensic Calibration Assistant"
                subtitle="Property Risk Assessment"
                iconPath="/icons/forensic-audit.png"
                fetchInitialMessage={() => Promise.resolve([{
                  id: '1',
                  role: 'assistant',
                  content: `I'm analyzing your ${propertyData?.year_built} property for maintenance risks. Let me ask a few quick questions to calibrate the forensic audit:\n\n1. Have you replaced the main sewer lateral in the last 15 years?\n\n2. What type of heating system does the property currently have?\n\n3. Any known electrical upgrades since you've owned it?`
                }])}
                handleSendMessage={(message) => {
                  // Simulate TCS improvement with each interaction
                  const newAccuracy = Math.min(diagnosticAccuracy + Math.floor(Math.random() * 15) + 10, 100);
                  setDiagnosticAccuracy(newAccuracy);
                  
                  // Trigger payment gate if TCS reaches 90+
                  if (newAccuracy >= 90 && !showPaymentTrigger) {
                    setShowPaymentTrigger(true);
                  }
                  
                  // Simulate forensic chat responses
                  return Promise.resolve({
                    id: Date.now().toString(),
                    role: 'assistant', 
                    content: `Got it. ${message.length > 50 ? 'Based on that detail,' : 'Thanks.'} I'm building your mechanical entropy profile. ${Math.random() > 0.6 ? 'One more question: Any recent foundation or structural work?' : 'Perfect - I have enough data to run the forensic analysis.'}`
                  });
                }}
                onCustomAction={(action) => {
                  if (action.type === 'calibration_complete') {
                    handleVerificationComplete();
                  }
                }}
                getChatStorageKey={() => 'forensic_audit_chat'}
                getChatTimestampKey={() => 'forensic_audit_timestamp'}
              />
            </ChatContainer>
          )}
        </VerificationSection>
      )}

      {auditPhase === 'generating' && (
        <GeneratingSection>
          <GeneratingCard>
            <Spin size="large" />
            <GeneratingTitle>Running Forensic Analysis...</GeneratingTitle>
            <GeneratingProgress>
              <Progress 
                percent={verificationProgress} 
                strokeColor="#ff5722"
                showInfo={false}
              />
              <ProgressLabel>
                {verificationProgress < 25 && 'Analyzing mechanical entropy...'}
                {verificationProgress >= 25 && verificationProgress < 50 && 'Detecting critical failure points...'}
                {verificationProgress >= 50 && verificationProgress < 75 && 'Calculating St. Louis cost estimates...'}
                {verificationProgress >= 75 && 'Generating 5-year CapEx forecast...'}
              </ProgressLabel>
            </GeneratingProgress>
          </GeneratingCard>
        </GeneratingSection>
      )}

      {auditPhase === 'preview' && auditReport && (
        <PreviewSection>
          <PreviewAlert>
            <Alert
              message="Forensic Analysis Complete"
              description="Critical maintenance risks detected. Sign up to unlock the full report and cost breakdown."
              type="warning"
              showIcon
              icon={<WarningOutlined />}
            />
          </PreviewAlert>

          <ReportPreviewCard>
            <PreviewHeader>
              <RiskScoreBadge $score={auditReport.risk_score}>
                Risk Score: {auditReport.risk_score}/100
              </RiskScoreBadge>
              <BlurOverlay />
            </PreviewHeader>

            <BlurredContent>
              <SectionTitle>🚨 Critical Failures Detected</SectionTitle>
              {auditReport.critical_failures.map((failure, idx) => (
                <FailureItem key={idx}>
                  <FailureHeader>
                    <FailureSystem>{failure.system}</FailureSystem>
                    <FailureUrgency $level={failure.urgency}>{failure.urgency}</FailureUrgency>
                  </FailureHeader>
                  <FailureIssue>{failure.issue}</FailureIssue>
                  <FailureCost>Estimated Cost: {failure.cost_range}</FailureCost>
                </FailureItem>
              ))}

              <SectionTitle>💰 5-Year Financial Forecast</SectionTitle>
              <ForecastAmount>${auditReport.five_year_forecast}</ForecastAmount>
              
              <SectionTitle>🔧 Local Vendor Estimates</SectionTitle>
              <VendorEstimate>Emergency Rate: {auditReport.vendor_estimates.emergency_rate}</VendorEstimate>
            </BlurredContent>

            <UnlockGate>
              <LockIcon><LockOutlined /></LockIcon>
              <UnlockTitle>Unlock Full Forensic Report</UnlockTitle>
              <UnlockBenefits>
                <BenefitItem><CheckCircleOutlined /> Complete system-by-system breakdown</BenefitItem>
                <BenefitItem><CheckCircleOutlined /> Maintenance timeline and priorities</BenefitItem>
                <BenefitItem><CheckCircleOutlined /> Local contractor recommendations</BenefitItem>
                <BenefitItem><CheckCircleOutlined /> Monthly property monitoring</BenefitItem>
              </UnlockBenefits>
              <UnlockButton type="primary" size="large" onClick={handleUnlockFullReport}>
                Sign Up to Unlock Report ($99/mo)
              </UnlockButton>
            </UnlockGate>
          </ReportPreviewCard>
        </PreviewSection>
      )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  padding: 20px;
`;

const AuditHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 40px;
  gap: 16px;
`;

const HeaderIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
`;

const HeaderContent = styled.div`
  text-align: left;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  letter-spacing: -0.02em;
`;

const HeaderSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 1.125rem;
  color: #64748b;
  font-weight: 500;
`;

const ClaimSection = styled.div`
  display: flex;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
`;

const ClaimCard = styled(Card)`
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  border: none;

  .ant-card-body {
    padding: 40px;
  }
`;

const ClaimTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  margin-bottom: 16px;
`;

const ClaimDescription = styled.p`
  font-size: 1.125rem;
  color: #64748b;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const AddressInputGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AddressInput = styled(Input)`
  flex: 1;
  border-radius: 8px;
  border: 2px solid #e2e8f0;
  
  &:focus {
    border-color: #ff5722;
    box-shadow: 0 0 0 2px rgba(245, 87, 34, 0.1);
  }
`;

const ClaimButton = styled(Button)`
  padding: 0 32px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
  border: none;
  font-weight: 600;
  
  &:hover {
    background: linear-gradient(135deg, #f4511e 0%, #e64a19 100%);
    transform: translateY(-1px);
  }
`;

const ValuePropsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
`;

const ValueProp = styled.div`
  text-align: center;
  padding: 20px;
`;

const PropIcon = styled.div`
  color: #ff5722;
  font-size: 2rem;
  margin-bottom: 12px;
`;

const PropTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const PropDesc = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
`;

const VerificationSection = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const VerificationCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 16px;
  border: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
`;

const VerificationTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 12px;
`;

const VerificationDesc = styled.p`
  color: #64748b;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const PropertySummary = styled.div`
  background: #f8fafc;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const SummaryTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`;

const SummaryDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
`;

const DetailItem = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
`;

const ChatInstructions = styled.div`
  background: #fffbeb;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 16px;
  font-size: 0.875rem;
  color: #92400e;
`;

const ChatContainer = styled.div`
  height: 400px;
  border-radius: 16px;
  overflow: hidden;
`;

const GeneratingSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const GeneratingCard = styled(Card)`
  text-align: center;
  border-radius: 16px;
  border: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  padding: 40px;
`;

const GeneratingTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 24px 0 32px;
`;

const GeneratingProgress = styled.div`
  width: 300px;
  margin: 0 auto;
`;

const ProgressLabel = styled.div`
  margin-top: 12px;
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
`;

const PreviewSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PreviewAlert = styled.div`
  margin-bottom: 24px;
`;

const ReportPreviewCard = styled(Card)`
  border-radius: 16px;
  border: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

const PreviewHeader = styled.div`
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
  padding: 24px;
  color: white;
  position: relative;
`;

const RiskScoreBadge = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 1.125rem;
`;

const BlurOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(2px);
  pointer-events: none;
`;

const BlurredContent = styled.div`
  padding: 32px;
  filter: blur(3px);
  position: relative;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px;
`;

const FailureItem = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
`;

const FailureHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const FailureSystem = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const FailureUrgency = styled.span`
  background: ${props => props.$level === 'Critical' ? '#dc2626' : '#ea580c'};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const FailureIssue = styled.div`
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
`;

const FailureCost = styled.div`
  color: #dc2626;
  font-weight: 600;
`;

const ForecastAmount = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #dc2626;
  margin-bottom: 24px;
`;

const VendorEstimate = styled.div`
  color: #374151;
  font-weight: 500;
`;

const UnlockGate = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  text-align: center;
  min-width: 400px;
  z-index: 10;
`;

const LockIcon = styled.div`
  color: #ff5722;
  font-size: 2rem;
  margin-bottom: 16px;
`;

const UnlockTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
`;

const UnlockBenefits = styled.div`
  text-align: left;
  margin-bottom: 24px;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #374151;
  
  .anticon {
    color: #10b981;
  }
`;

const UnlockButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff5722 0%, #f4511e 100%);
  border: none;
  font-weight: 600;
  font-size: 1rem;
  
  &:hover {
    background: linear-gradient(135deg, #f4511e 0%, #e64a19 100%);
    transform: translateY(-1px);
  }
`;

const DiagnosticAccuracyCard = styled(Card)`
  margin-top: 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const DiagnosticHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const DiagnosticTitle = styled.h4`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
`;

const DiagnosticProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DiagnosticScore = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => props.accurate ? '#52c41a' : '#666'};
`;

const DiagnosticLabel = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.accurate ? '#52c41a' : '#666'};
`;

const PaymentTriggerAlert = styled.div`
  margin-top: 16px;
  
  .ant-alert {
    border-radius: 8px;
    border: 1px solid #52c41a;
  }
`;

export default ForensicShadowAudit;